import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { BRAND, telLink, waLink } from "@/lib/brand";

const empty = {
  student_name: "",
  parent_name: "",
  student_class: "",
  board: "",
  subjects: "",
  phone: "",
  whatsapp: "",
  email: "",
  preferred_batch: "Flexible",
  heard_from: "",
  message: "",
};

export default function ContactPage() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.student_name || !form.parent_name || !form.student_class || !form.board || !form.subjects || !form.phone) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.whatsapp) delete payload.whatsapp;
      await api.post("/enquiries", payload);
      toast.success("Enquiry received!", { description: "Our team will contact you within 24 hours." });
      setForm(empty);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="bg-[#0B192C] text-white relative overflow-hidden">
        <div className="vk-grain" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <span className="vk-chip">Contact &amp; admissions</span>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl font-800 tracking-tight max-w-3xl">Let&apos;s get your child started.</h1>
          <p className="mt-5 max-w-2xl text-lg text-white/70">Fill the enquiry form or reach us directly — we&apos;ll set up a free demo class within 48 hours.</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
          <div className="space-y-6">
            <div>
              <span className="vk-chip">Reach us</span>
              <h2 className="mt-3 font-display text-3xl font-800 text-[#0B192C]">Visit our centre in Rajeev Nagar</h2>
            </div>
            <ul className="space-y-4 text-slate-700">
              <li className="flex gap-3"><MapPin className="text-[#FF6B35] mt-0.5" size={20} /><span>{BRAND.address}</span></li>
              <li className="flex gap-3"><Phone className="text-[#FF6B35] mt-0.5" size={20} /><span><a href={telLink} className="hover:text-[#FF6B35]">{BRAND.phonePrimary}</a> · {BRAND.phoneSecondary}</span></li>
              <li className="flex gap-3"><Mail className="text-[#FF6B35] mt-0.5" size={20} /><a href={`mailto:${BRAND.email}`} className="hover:text-[#FF6B35]">{BRAND.email}</a></li>
              <li className="flex gap-3"><Clock className="text-[#FF6B35] mt-0.5" size={20} /><span>Mon – Sat · 9:00 AM – 8:00 PM</span></li>
            </ul>
            <div className="flex flex-wrap gap-3 pt-2">
              <a href={waLink()} target="_blank" rel="noreferrer" className="vk-btn-primary">WhatsApp Now <ArrowRight size={16} /></a>
              <a href={telLink} className="vk-btn-dark">Call Now</a>
            </div>
            <div className="rounded-lg overflow-hidden border border-slate-200 aspect-[16/10] mt-4">
              <iframe
                title="Vikramshila Classes location on Google Maps"
                width="100%" height="100%"
                loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=Rajeev+Nagar+Patna+800024&output=embed"
              />
            </div>
          </div>

          <form onSubmit={onSubmit} data-testid="enquiry-form" className="rounded-lg border border-slate-200 bg-[#F5F7FA] p-8 space-y-5">
            <div>
              <span className="vk-chip">Admission enquiry</span>
              <h2 className="mt-3 font-display text-3xl font-800 text-[#0B192C]">Book a free demo class</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="student_name">Student Name *</Label>
                <Input id="student_name" data-testid="input-student-name" value={form.student_name} onChange={update("student_name")} required className="mt-1 bg-white" />
              </div>
              <div>
                <Label htmlFor="parent_name">Parent Name *</Label>
                <Input id="parent_name" data-testid="input-parent-name" value={form.parent_name} onChange={update("parent_name")} required className="mt-1 bg-white" />
              </div>
              <div>
                <Label>Class *</Label>
                <Select value={form.student_class} onValueChange={(v) => setForm(f=>({...f, student_class: v}))}>
                  <SelectTrigger data-testid="select-class" className="mt-1 bg-white"><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>
                    {["VI","VII","VIII","IX","X"].map((c)=>(<SelectItem key={c} value={c}>Class {c}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Board *</Label>
                <Select value={form.board} onValueChange={(v) => setForm(f=>({...f, board: v}))}>
                  <SelectTrigger data-testid="select-board" className="mt-1 bg-white"><SelectValue placeholder="CBSE / ICSE" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CBSE">CBSE</SelectItem>
                    <SelectItem value="ICSE">ICSE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="subjects">Subjects Interested In *</Label>
                <Input id="subjects" data-testid="input-subjects" value={form.subjects} onChange={update("subjects")} placeholder="e.g. Maths, Science, English" required className="mt-1 bg-white" />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input id="phone" data-testid="input-phone" value={form.phone} onChange={update("phone")} required className="mt-1 bg-white" />
              </div>
              <div>
                <Label htmlFor="whatsapp">WhatsApp Number</Label>
                <Input id="whatsapp" data-testid="input-whatsapp" value={form.whatsapp} onChange={update("whatsapp")} className="mt-1 bg-white" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={update("email")} className="mt-1 bg-white" />
              </div>
              <div>
                <Label>Preferred Batch</Label>
                <Select value={form.preferred_batch} onValueChange={(v) => setForm(f=>({...f, preferred_batch: v}))}>
                  <SelectTrigger data-testid="select-batch" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Morning">Morning</SelectItem>
                    <SelectItem value="Evening">Evening</SelectItem>
                    <SelectItem value="Flexible">Flexible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>How did you hear about us?</Label>
                <Select value={form.heard_from} onValueChange={(v) => setForm(f=>({...f, heard_from: v}))}>
                  <SelectTrigger data-testid="select-heard" className="mt-1 bg-white"><SelectValue placeholder="Choose" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Google">Google</SelectItem>
                    <SelectItem value="Friend">Friend / Family</SelectItem>
                    <SelectItem value="Banner">Banner / Poster</SelectItem>
                    <SelectItem value="Social">Social Media</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="message">Message / Query</Label>
                <Textarea id="message" data-testid="input-message" value={form.message} onChange={update("message")} rows={4} className="mt-1 bg-white" />
              </div>
            </div>

            <button type="submit" disabled={submitting} data-testid="submit-enquiry" className="vk-btn-primary w-full justify-center">
              {submitting ? "Submitting..." : (<>Submit Enquiry <Send size={16} /></>)}
            </button>
            <p className="text-xs text-slate-500 text-center">By submitting, you agree to be contacted by Vikramshila Classes for admission enquiry.</p>
          </form>
        </div>
      </section>
    </div>
  );
}
